import styles from './AnnouncementModal.module.scss';
import Modal from '../Modal';
import { loader } from 'graphql.macro';
import { useMutation } from '@apollo/client';
import moment from 'moment';
import { useRef, useState } from 'react';
import RichTextEditor from '../../rich_text/RichTextEditor';
import { showToast } from '../../../graphql/cache/modal';
import SimpleUserCard from '../../SimpleUserCard';
import { Text } from '../../typography';
import { useTranslation } from 'react-i18next';
import { keys } from '../../../utilities/translator/translation_keys';
import { useModal } from '../../../providers/ModalProvider';
import { modals } from '../../../providers/modals';
import SimpleFileCard from '../../files/SimpleFileCard';
import AnnouncementSignaturePad from '../../AnnouncementSignaturePad';

const snoozeAnnouncementMutation = loader('./AnnouncementModal.snooze.graphql');
const acceptAnnouncementMutation = loader('./AnnouncementModal.accept.graphql');

export default function AnnouncementModal() {
  const { t } = useTranslation();
  const { modalState, closeModal } = useModal();
  const { show, announcement, id, status, signatureUrl } =
    modalState.announcementModal;
  const [snoozeAnnouncement] = useMutation(snoozeAnnouncementMutation);
  const [acceptAnnouncement] = useMutation(acceptAnnouncementMutation);
  const { creator, dateOfRelease } = announcement || {};
  const onHide = () => closeModal({ modalName: modals.announcement });
  const files = announcement?.files || [];
  const signaturePadRef = useRef();
  const [hasSignature, setHasSignature] = useState(false);
  const needsSignature =
    !!announcement?.requireSignature && status !== 'READ' && !signatureUrl;

  return (
    <Modal
      cancelText={t(keys.announcements.SNOOZE)}
      submitText={t(keys.announcements.ACCEPT)}
      variant="success"
      title={announcement?.title}
      hideCancel={status !== 'UNREAD'}
      hideSubmit={status === 'READ'}
      submitDisabled={needsSignature && !hasSignature}
      disableCloseOnSubmit={true}
      onSubmit={async () => {
        const newSignatureUrl = needsSignature
          ? await signaturePadRef.current?.getSignatureUrl()
          : undefined;
        acceptAnnouncement({
          variables: {
            id,
            dateReleased: new moment(),
            signatureUrl: newSignatureUrl,
          },
        });
        onHide();
      }}
      onClose={onHide}
      onCancel={() => {
        if (status === 'READ' || status === 'SNOOZED') {
          onHide();
        } else {
          snoozeAnnouncement({
            variables: {
              id,
              dateReleased: moment().add(1, 'days').format('yyyy-MM-DD'),
            },
          }).then(() => {
            showToast({
              title: t(keys.announcements.ANNOUNCEMENT_SNOOZED),
              message: t(keys.announcements.SNOOZED_MESSAGE),
            });
          });
        }
        onHide();
      }}
      open={show}
    >
      <div className={styles.content}>
        <RichTextEditor
          readOnly
          onChange={() => {}}
          id={announcement?.id}
          value={announcement?.content}
        />
        <div className={styles.modalText}>
          <SimpleUserCard user={creator} />
          <Text noMargin weight="semiBold" color="secondary">
            {dateOfRelease}
          </Text>
        </div>
      </div>
      <div className={styles.fileList}>
        {files?.map((f, i) => (
          <SimpleFileCard
            {...f}
            fileName={f?.name}
            file={f}
            key={`${f?.id}-file-${i}`}
          />
        ))}
      </div>
      {!!announcement?.requireSignature && (
        <AnnouncementSignaturePad
          ref={signaturePadRef}
          value={signatureUrl}
          onSignedChange={setHasSignature}
        />
      )}
    </Modal>
  );
}
